import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Copy, Check, Globe, ShieldCheck } from 'lucide-react'
import { DeleteAccountCard } from './DeleteAccountCard'
import { AccountSecurityCard } from './AccountSecurityCard'
import type { JournalistProfileState } from './useJournalistProfile'

export function ProfileSettingsTab({ state }: { state: JournalistProfileState }) {
  const { profile, form, setForm, editing, setEditing, authUserId, authEmail } = state
  const [copied, setCopied] = useState(false)
  const publicUrl = profile?.slug ? `${window.location.origin}/press/${profile.slug}` : null

  const toggle = (key: 'contact_visible_everyone' | 'profile_public') => (v: boolean) => {
    if (!editing) setEditing(true)
    setForm(f => ({ ...f, [key]: v }))
  }

  return (
    <div className="space-y-5">
      <AccountSecurityCard email={authEmail} />

      <Card>
        <CardHeader><CardTitle className="text-base">Visibility</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-4 p-4 bg-muted rounded-lg">
            <ShieldCheck className="w-4 h-4 text-success mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground">Contact details visible to accrediting institutions</p>
              <p className="text-xs text-muted-foreground mt-1 text-justify">
                Sharing your contact details is a requirement of accreditation, so this can't be turned off. Communications Officers at organisations you're accredited to (or have applied to) can always see them — they cannot share this information with other organisations.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-muted rounded-lg">
            <Switch checked={form.contact_visible_everyone} onCheckedChange={toggle('contact_visible_everyone')} className="mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Make contact details visible to everyone</p>
              <p className="text-xs text-muted-foreground mt-1 text-justify">
                Institutions you haven't applied to or been accredited by can only message you by default. Enable this to let any institution browsing the journalist directory see your contact details too — otherwise your contact stays locked for them and they can only reach you via chat.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-muted rounded-lg">
            <Switch checked={form.profile_public} onCheckedChange={toggle('profile_public')} className="mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium">Public journalist profile</p>
              <p className="text-xs text-muted-foreground mt-1 text-justify">
                Enable to publish a public profile page. Institutions and editors can verify your accreditations and coverage score.
              </p>
              {form.profile_public && publicUrl && (
                <div className="mt-2 flex items-center gap-2">
                  <code className="text-xs bg-background border border-border rounded px-2 py-1 text-success flex-1 truncate">
                    {publicUrl}
                  </code>
                  <button
                    type="button"
                    onClick={() => { navigator.clipboard.writeText(publicUrl); setCopied(true); setTimeout(() => setCopied(false), 2000) }}
                    className="p-1.5 rounded border border-border bg-background hover:bg-muted transition-colors"
                    aria-label="Copy public profile link"
                  >
                    {copied ? <Check className="w-3 h-3 text-success" /> : <Copy className="w-3 h-3 text-muted-foreground" />}
                  </button>
                  <a
                    href={`/press/${profile?.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded border border-border bg-background hover:bg-muted transition-colors"
                    aria-label="Open public profile"
                  >
                    <Globe className="w-3 h-3 text-muted-foreground" />
                  </a>
                </div>
              )}
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Visibility changes are applied when you save.
          </p>
        </CardContent>
      </Card>

      <DeleteAccountCard userId={authUserId} email={authEmail} fullName={profile?.full_name} />
    </div>
  )
}
